import {
  type OperatorBrand,
  OTHER_OPERATOR_SLUG,
  operatorSearchText,
  TRACKED_OPERATORS,
} from '@ev/shared'
import { foldText, searchTokens, tokenScore } from './fold'

/**
 * Matching what was typed against the operator registry.
 *
 * Typing "Orlen" is almost never a request for a particular Orlen forecourt -
 * it is a request for *Orlen*, the network. The site list answers it with the
 * few hundred sites that happen to sort first, which is the least useful thing
 * the box could do with a brand name. So brands are matched separately and
 * offered above the sites, and choosing one toggles the operator filter rather
 * than flying the camera somewhere.
 *
 * The `other` bucket is left out. It is not a brand a driver can mean: its
 * display name ("Inni operatorzy") is a label this app invented for everything
 * the registry does not track, so offering it as a suggestion would answer a
 * brand search with a bucket the searcher has never heard of. It stays a
 * checkbox in the filter panel, where the label has its column to explain it.
 */

/** How many brands the box offers before the site results. */
export const OPERATOR_SUGGESTION_LIMIT = 3

/**
 * Folded search text per brand - display name plus registry aliases - computed
 * once for the registry rather than once per keystroke.
 */
const FOLDED_BRANDS: readonly { brand: OperatorBrand; folded: string }[] = TRACKED_OPERATORS.map(
  (brand) => ({ brand, folded: foldText(operatorSearchText(brand)) }),
)

/**
 * Brands matching the query, best match first.
 *
 * Every token must match, as in the site search, so "green way" narrows rather
 * than widening. Ties break on display name, so the order is stable between
 * keystrokes.
 */
export const searchOperators = (
  query: string,
  limit: number = OPERATOR_SUGGESTION_LIMIT,
): OperatorBrand[] => {
  const tokens = searchTokens(query)
  if (tokens.length === 0) return []

  const hits: { brand: OperatorBrand; score: number }[] = []

  FOLDED_BRANDS.forEach(({ brand, folded }) => {
    if (brand.slug === OTHER_OPERATOR_SLUG) return

    let total = 0
    let matchedAll = true
    tokens.forEach((token) => {
      const score = tokenScore(folded, token)
      if (Number.isFinite(score)) total += score
      else matchedAll = false
    })

    if (matchedAll) hits.push({ brand, score: total })
  })

  hits.sort((a, b) => a.score - b.score || a.brand.displayName.localeCompare(b.brand.displayName))
  return hits.slice(0, limit).map((hit) => hit.brand)
}
